import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Clock } from 'lucide-react';
import { useCourseApi, Course } from '../hooks/useCourseApi';
import CourseCard from '../components/course/CourseCard';

const GradeCoursesPage: React.FC = () => {
  const { grade } = useParams<{ grade: string }>();
  const location = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const { getCourses, fetchEnrolledCourseIds, isLoading, error } = useCourseApi();

  // Get grade from params or location state
  const gradeValue = location.state?.grade || 
    (grade?.toLowerCase() === 'secondary1' ? 'Secondary1' : 
     grade?.toLowerCase() === 'secondary2' ? 'Secondary2' : 
     grade?.toLowerCase() === 'secondary3' ? 'Secondary3' : 'Secondary1');

  const gradeTitle = 
    gradeValue === 'Secondary1' ? 'الصف الأول الثانوي' :
    gradeValue === 'Secondary2' ? 'الصف الثاني الثانوي' :
    gradeValue === 'Secondary3' ? 'الصف الثالث الثانوي' : 'الصف الأول الثانوي';

  useEffect(() => {
    fetchCourses();
    fetchEnrolledIds();
  }, [gradeValue, pageNumber]);

  const fetchCourses = async () => {
    setIsLoadingCourses(true);
    try {
      const result = await getCourses(gradeValue, pageNumber, 10);
      
      if (result?.success && result?.data) {
        setCourses(result.data.courses || []);
        setTotalPages(result.data.totalPages || 1);
      } else {
        setCourses([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setCourses([]);
      setTotalPages(1);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const fetchEnrolledIds = async () => {
    try {
      const enrolledIds = await fetchEnrolledCourseIds();
      setEnrolledCourseIds(enrolledIds);
    } catch (err) {
      console.error('Error fetching enrolled course IDs:', err);
      setEnrolledCourseIds([]);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Header Section */}
      <div className="py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-gray-500 mb-8" dir="ltr">
            <Link 
              to="/" 
              className="hover:text-orange-500 transition-colors duration-200 flex items-center"
            >
              الرئيسية
              <ArrowRight className="w-4 h-4 mx-2" />
            </Link>
            <span className="text-gray-900">كورسات {gradeTitle}</span>
          </nav>

        </div>
      </div>

      {/* Courses Section */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoadingCourses && (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center">
              <div className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto absolute inset-0 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                </div>
                <p className="text-gray-600 text-lg font-medium">جاري تحميل الكورسات...</p>
              </div>
            </div>
          )}

          {!isLoadingCourses && error && (
            <div className="text-center py-12">
              <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md mx-auto shadow-sm">
                <p className="text-gray-800 font-cairo-medium mb-4">{error}</p>
                <button
                  onClick={fetchCourses}
                  className="px-6 py-2 bg-white border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors duration-200 font-cairo-medium"
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          )}

          {!isLoadingCourses && !error && courses.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-cairo-medium">
                لا توجد كورسات متاحة لـ{gradeTitle} حالياً
              </p>
            </div>
          )}

          {!isLoadingCourses && !error && courses.length > 0 && (
            <>
              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {courses.map((course) => (
                  <CourseCard 
                    key={course.id} 
                    course={course} 
                    isEnrolled={enrolledCourseIds.includes(course.id)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <div className="inline-flex rounded-lg border border-gray-200 divide-x rtl:divide-x-reverse">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 transition-colors duration-200 font-cairo-medium ${
                          pageNumber === page
                            ? 'bg-orange-50 text-orange-600'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GradeCoursesPage;
